import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Vote, ScrollText, Landmark, Users, ShieldCheck, Gavel, LogOut, ArrowRight, Mic, BookOpen,
} from "lucide-react";
import api, { clearBoardroomToken, getBoardroomToken } from "../utils/qccaApi";

const TOOLS = [
  { icon: Vote, title: "Secure Vote-by-Email", status: "Live", to: "/qcca/boardroom/votes", testid: "tool-votes",
    body: "Create a tokenized ballot per unit owner, send, track turnout, publish transparent results." },
  { icon: Mic, title: "Digital Clerk", status: "Live", to: "/qcca/boardroom/clerk", testid: "tool-clerk",
    body: "Record or upload your meeting, auto-transcribe, and get structured minutes — motions, action items, decisions." },
  { icon: BookOpen, title: "Digital Minute Book", status: "Live", to: "/qcca/boardroom/minutebook", testid: "tool-minutebook",
    body: "A tidy, searchable record of every meeting — auto-filed, editable, email to the board in one click." },
  { icon: ScrollText, title: "Template Vault", status: "Coming soon", to: "/qcca/boardroom/templates", testid: "tool-templates",
    body: "Meeting minutes, civil-enforcement packages, bylaw templates — peer-reviewed." },
  { icon: Landmark, title: "Director Forum", status: "Coming soon", to: "/qcca/boardroom/forum", testid: "tool-forum",
    body: "Board-only discussion for legal strategy, precedent, and structural issues." },
  { icon: Users, title: "Live Board Room", status: "Coming soon", to: "/qcca/boardroom/room", testid: "tool-room",
    body: "Encrypted live meetings — emergency quorums, inter-corp strategy sessions." },
  { icon: ShieldCheck, title: "Property Manager Ratings", status: "Coming soon", to: "/qcca/boardroom/ratings", testid: "tool-ratings",
    body: "Crowdsourced, verified-director-only ratings of PM firms and service providers." },
  { icon: Gavel, title: "City Policy Alerts", status: "Coming soon", to: "/qcca/boardroom/alerts", testid: "tool-alerts",
    body: "Automated alerts when the City puts anything condo-related on the agenda." },
];

export default function QCCABoardroom() {
  const nav = useNavigate();
  const [director, setDirector] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!getBoardroomToken()) {
      nav("/qcca/boardroom/login");
      return;
    }
    api.get("/qcca/boardroom/me")
      .then((r) => setDirector(r.data))
      .catch(() => { clearBoardroomToken(); nav("/qcca/boardroom/login"); });
    api.get("/qcca/votes").then((r) => setStats({ total: r.data.count })).catch(() => {});
  }, [nav]);

  const logout = () => { clearBoardroomToken(); nav("/"); };

  if (!director) return null;

  return (
    <div className="min-h-screen relative z-10 bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-black/90 backdrop-blur-md border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">QCCA · Boardroom</div>
            <div className="font-headline text-white text-base leading-none mt-1">{director.full_name}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">{director.board_position.replace(/_/g, " ")}</div>
              <div className="font-mono text-[11px] text-gray-400 mt-0.5">{director.condo_corporation_name}</div>
            </div>
            <button onClick={logout}
              className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-boardroom-logout">
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Welcome */}
        <div className="mb-12">
          <h1 className="font-headline text-4xl lg:text-5xl text-white leading-tight">
            Welcome back, <span className="italic text-[#C5A52A]">{director.full_name.split(" ")[0]}.</span>
          </h1>
          <p className="mt-4 text-gray-400 max-w-2xl leading-relaxed">
            This is the QCCA Boardroom — the private workspace for verified elected directors. Start by running a secure vote, or explore the tools rolling out across 2026.
          </p>
        </div>

        {/* Tool grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="qcca-boardroom-tools">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const isLive = t.status === "Live";
            const card = (
              <div className={`group h-full relative bg-[#0a0a0a] border p-7 transition-colors ${
                isLive ? "border-[#C5A52A]/60 hover:border-[#C5A52A]" : "border-[#1a1a1a] hover:border-[#C5A52A]/40"
              }`}>
                <div className="flex items-start justify-between gap-4">
                  <Icon className="w-7 h-7 text-[#C5A52A]" />
                  <span className={`font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border ${
                    isLive ? "border-[#C5A52A] text-[#C5A52A] bg-[#C5A52A]/5" : "border-[#1a1a1a] text-gray-500"
                  }`}>{t.status}</span>
                </div>
                <h3 className="mt-6 font-headline text-xl text-white leading-tight">{t.title}</h3>
                <p className="mt-3 text-gray-400 text-sm leading-relaxed">{t.body}</p>
                <div className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A] group-hover:text-white transition-colors">
                  {isLive ? "Open" : "Preview"} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            );
            return (
              <Link key={t.title} to={t.to} className="block" data-testid={`qcca-boardroom-${t.testid}`}>
                {card}
              </Link>
            );
          })}
        </div>

        {/* Quick stats */}
        {stats && (
          <div className="mt-14 border border-[#1a1a1a] bg-[#0a0a0a] p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Your activity</div>
            <div className="mt-4 grid sm:grid-cols-3 gap-6">
              <div>
                <div className="font-headline text-4xl text-white">{stats.total}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 mt-1">Votes run</div>
              </div>
              <div>
                <div className="font-headline text-4xl text-[#C5A52A]">{director.board_position.replace(/_/g, " ")}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 mt-1">Your role</div>
              </div>
              <div>
                <div className="font-headline text-xl text-white leading-tight break-words">{director.condo_corporation_name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 mt-1">Corporation</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
